import { useEffect, useState } from "react";
import { Search, MapPin, ChevronLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { VisitReportForm } from "@/components/tech/VisitReportForm";

const CustomerRow = ({ customer, onPick }) => (
  <button type="button" onClick={() => onPick(customer)} data-testid={`tech-customer-${customer.id}`}
    className="w-full text-left bg-white border border-zinc-200 hover:border-brand rounded-sm p-4 transition-colors duration-200">
    <p className="font-bold text-brand-ink">{customer.name}</p>
    {customer.locations.length > 0 ? (
      customer.locations.map((l) => (
        <p key={l.id} className="text-sm text-zinc-600 mt-0.5 flex items-start gap-1.5">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand" /> {l.label} — {l.address}
        </p>
      ))
    ) : (
      <p className="text-sm text-zinc-500 mt-0.5">No saved location</p>
    )}
  </button>
);

const LocationPicker = ({ customer, onPick }) => (
  <div className="space-y-2">
    <p className="text-sm font-bold text-brand-ink">Which location did you service?</p>
    {customer.locations.map((l) => (
      <button key={l.id} type="button" onClick={() => onPick(l)} data-testid={`tech-location-${l.id}`}
        className="w-full text-left bg-white border border-zinc-200 hover:border-brand rounded-sm p-4 transition-colors duration-200">
        <p className="font-medium text-brand-ink flex items-center gap-2"><MapPin className="w-4 h-4 text-brand" /> {l.label}</p>
        <p className="text-sm text-zinc-600 mt-0.5">{l.address}</p>
      </button>
    ))}
  </div>
);

export const TechnicianPortal = () => {
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [location, setLocation] = useState(null);
  const [picked, setPicked] = useState(false);

  useEffect(() => {
    let stale = false;
    api.get("/tech/customers", { params: { q: query } })
      .then((r) => { if (!stale) setCustomers(r.data.customers); })
      .catch(() => { if (!stale) setCustomers([]); });
    return () => { stale = true; };
  }, [query]);

  const pickCustomer = (c) => {
    setCustomer(c);
    if (c.locations.length === 1) {
      setLocation(c.locations[0]);
      setPicked(true);
    } else if (c.locations.length === 0) {
      setLocation(null);
      setPicked(true);
    }
  };

  const reset = () => { setCustomer(null); setLocation(null); setPicked(false); };

  if (customer && picked) {
    return (
      <div>
        <button type="button" onClick={reset} data-testid="tech-back-btn"
          className="text-sm font-bold text-brand flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Change customer
        </button>
        <VisitReportForm customer={customer} location={location} onDone={reset} />
      </div>
    );
  }

  if (customer) {
    return (
      <div>
        <button type="button" onClick={reset} data-testid="tech-back-btn"
          className="text-sm font-bold text-brand flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Change customer
        </button>
        <p className="font-bold text-brand-ink mb-3">{customer.name}</p>
        <LocationPicker customer={customer} onPick={(l) => { setLocation(l); setPicked(true); }} />
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm font-bold text-brand-ink mb-2">Find the customer</p>
      <div className="relative">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="tech-customer-search-input" placeholder="Search by name or address..."
          value={query} onChange={(e) => setQuery(e.target.value)} className="rounded-sm pl-9 h-12 bg-white" />
      </div>
      <div className="mt-3 space-y-2">
        {customers === null && <Loader2 className="w-6 h-6 text-brand animate-spin" />}
        {customers?.length === 0 && <p data-testid="tech-customers-empty" className="text-sm text-zinc-600">No customers found.</p>}
        {customers?.map((c) => <CustomerRow key={c.id} customer={c} onPick={pickCustomer} />)}
      </div>
    </div>
  );
};
